import { Tag } from 'antd'
import { CASE_STATUSES, CASE_STATUS_KEYS, type CaseStatus } from '@/constants/enums'

type PresetColor = 'success' | 'processing' | 'error' | 'warning' | 'default'

/**
 * 案件ステータス（CaseStatus）に対する色マッピング。
 * enum key で持つことで、表示ラベル変更があっても色付けが追従する。
 */
const CASE_STATUS_COLOR_BY_KEY: Record<CaseStatus, PresetColor> = {
  IDENT_ID_UNEXPORTED: 'default',
  APP_AWAITING_SUBMIT: 'processing',
  APP_UNDER_REVIEW: 'warning',
  APP_REJECTED: 'warning',
  AWAITING_TRANSFER_LIST: 'processing',
  AWAITING_CALC: 'processing',
  CALC_IN_PROGRESS: 'processing',
  CALC_ERROR: 'error',
  CALC_DONE: 'success',
  // 品川区指摘(検討事項 No.10): 作成済と出力済が見分けづらい。
  // 出力済を客先要望により「白字＋ベタ青背景」(#4F81BD HEX指定) に変更したことで
  // 判別容易になったため、作成済は元の薄青(processing)へ戻す。
  NOTICE_PRINTED: 'processing',
  NOTICE_DOWNLOADED: '#4F81BD' as PresetColor,
  ZENGIN_PROD_EXPORTED: 'success',
  COMPLETED_CASH: 'success',
  COMPLETED_DENIED: 'error',
  COMPLETED_PLAINTIFF_CALC: 'default',
}

/**
 * 案件ステータス以外で使われる日本語ラベル → preset 色のマップ。
 * 有効/無効、取込中、ロール表示、申請書 has_defect 等の汎用ステータス。
 */
const GENERIC_STATUS_COLORS: Record<string, PresetColor> = {
  '有効': 'success',
  '無効': 'error',
  '停止': 'error',
  '完了': 'success',
  '処理中': 'processing',
  'エラー': 'error',
  '取込エラー': 'error',
  'エラーあり': 'error',
  '確認済': 'success',
  '取込中': 'processing',
  '未対応': 'warning',
  '対応中': 'processing',
  '対応済': 'success',
  '未振付': 'default',
  '振付済': 'success',
  '管理者': 'purple' as PresetColor,
  '一般': 'blue' as PresetColor,
  '閲覧者': 'default',
  'システム管理者': 'purple' as PresetColor,
  '保護受給中': 'success',
  '給付済': 'success',
  '未給付': 'error',
  '審査中': 'warning',
}

const COLOR_MAP: Record<string, string> = {
  success: 'green',
  processing: 'blue',
  error: 'red',
  warning: 'gold',
  default: 'default',
}

function resolvePresetForCaseStatusLabel(label: string): PresetColor | undefined {
  for (const key of CASE_STATUS_KEYS) {
    if (CASE_STATUSES[key] === label) {
      return CASE_STATUS_COLOR_BY_KEY[key]
    }
  }
  return undefined
}

interface StatusBadgeProps {
  status: string
  color?: string
}

export default function StatusBadge({ status, color }: StatusBadgeProps) {
  const preset = resolvePresetForCaseStatusLabel(status) ?? GENERIC_STATUS_COLORS[status]
  const tagColor = color ?? (preset ? COLOR_MAP[preset] ?? preset : 'default')
  return <Tag color={tagColor}>{status}</Tag>
}
